import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
  Checkbox,
  Autocomplete,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import { ApiClient } from '../services/api';
import { env } from '../config/env';
import type {
  StudentProgressItem,
  TranscriptCourse,
  StudentProgressQueryParams,
} from '../services/api';
import { studentProgressToCSV, downloadCSV } from '../utils/csvExport';
import { formatUtcDate } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';
import { usePartner } from '../contexts/PartnerContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { SelectPartnerPrompt } from '../components/SelectPartnerPrompt';

type SortColumn = 'last_name' | 'percent_completed' | 'certification_date' | 'last_activity';
type SortOrder = 'asc' | 'desc';

// --- Status filter options ---
const STATUS_OPTIONS = [
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Certified', value: 'completed' },
];

export function Students(): React.ReactElement {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { idToken } = useAuth();
  const { activePartnerId: partnerId } = usePartnerScope();
  const { institutionName } = usePartner();
  const PARTNER_ID = partnerId ?? 0;
  const studentApi = useMemo(() => new ApiClient(idToken, env.studentApiUrl), [idToken]);
  const transcriptApi = useMemo(() => new ApiClient(idToken, env.transcriptApiUrl), [idToken]);

  const [students, setStudents] = useState<StudentProgressItem[]>([]);
  const [summaryData, setSummaryData] = useState<{
    distinct_students: number;
    active_courses: number;
  }>({ distinct_students: 0, active_courses: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Query state
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 50;
  const [sortColumn, setSortColumn] = useState<SortColumn>('last_activity');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Filters
  const [statusFilter, setStatusFilter] = useState<{ label: string; value: string } | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [availableCourses, setAvailableCourses] = useState<TranscriptCourse[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<TranscriptCourse[]>([]);

  // Checkbox selection — keeps the full row (not just the id) so a
  // cross-page selection can be exported after the page data is gone
  const [selectedRows, setSelectedRows] = useState<Map<number, StudentProgressItem>>(new Map());

  // CSV export
  const [exporting, setExporting] = useState(false);

  // Fetch courses on mount
  useEffect(() => {
    if (partnerId == null) return;
    transcriptApi
      .getTranscriptCourses(PARTNER_ID)
      .then((res) => setAvailableCourses(res.items))
      .catch((err) => console.error('[Students] Failed to fetch courses:', err));
  }, [transcriptApi, partnerId]);

  const buildParams = useCallback((): StudentProgressQueryParams => {
    const params: StudentProgressQueryParams = {
      page: page + 1,
      per_page: rowsPerPage,
      sort: sortColumn,
      order: sortOrder,
    };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter.value;
    if (dateFrom) params.date_from = dateFrom.format('YYYY-MM-DD');
    if (dateTo) params.date_to = dateTo.format('YYYY-MM-DD');
    if (selectedCourses.length > 0) {
      params.course_ids = selectedCourses.flatMap((c) => c.ids ?? [c.id]).join(',');
    }
    return params;
  }, [
    page,
    rowsPerPage,
    sortColumn,
    sortOrder,
    search,
    statusFilter,
    dateFrom,
    dateTo,
    selectedCourses,
  ]);

  const fetchStudents = useCallback(async () => {
    if (partnerId == null) return;
    try {
      setLoading(true);
      setError(null);
      const data = await studentApi.getStudentProgress(PARTNER_ID, buildParams());
      setStudents(data.items);
      setSummaryData({
        distinct_students: data.total,
        active_courses: data.summary.active_courses,
      });
    } catch (err) {
      console.error('[Students] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [studentApi, buildParams, partnerId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const displayedStudents = students;

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder(column === 'last_name' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const handleClearDateFrom = () => {
    setDateFrom(null);
    setPage(0);
  };
  const handleClearDateTo = () => {
    setDateTo(null);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedCourses([]);
    setPage(0);
  };

  const hasActiveFilters =
    !!search || !!statusFilter || !!dateFrom || !!dateTo || selectedCourses.length > 0;

  // Checkbox handlers
  const handleSelectAll = (checked: boolean) => {
    const newMap = new Map(selectedRows);
    displayedStudents.forEach((s) => {
      if (checked) {
        newMap.set(s.user_id, s);
      } else {
        newMap.delete(s.user_id);
      }
    });
    setSelectedRows(newMap);
  };

  const handleSelectRow = (student: StudentProgressItem) => {
    const newMap = new Map(selectedRows);
    if (newMap.has(student.user_id)) {
      newMap.delete(student.user_id);
    } else {
      newMap.set(student.user_id, student);
    }
    setSelectedRows(newMap);
  };

  const allPageSelected =
    displayedStudents.length > 0 && displayedStudents.every((s) => selectedRows.has(s.user_id));
  const somePageSelected = displayedStudents.some((s) => selectedRows.has(s.user_id));

  // CSV export
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      setError(null);
      const today = new Date().toISOString().slice(0, 10);

      // Checked students win over filters (SOPHIA-35147); built client-side
      // from the cached rows so the export matches what the table showed
      if (selectedRows.size > 0) {
        downloadCSV(
          studentProgressToCSV(Array.from(selectedRows.values())),
          `StudentProgress_${today}.csv`
        );
        return;
      }

      // None checked: export the same result set the table shows — all
      // filters, no pagination
      const filters: StudentProgressQueryParams = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter.value;
      if (dateFrom) filters.date_from = dateFrom.format('YYYY-MM-DD');
      if (dateTo) filters.date_to = dateTo.format('YYYY-MM-DD');
      if (selectedCourses.length > 0) {
        filters.course_ids = selectedCourses.flatMap((c) => c.ids ?? [c.id]).join(',');
      }
      const blob = await studentApi.downloadStudentExport(PARTNER_ID, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `StudentProgress_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Students] CSV export failed:', err);
      setError(
        `Failed to export CSV${err instanceof Error ? ` — ${err.message}` : ''}. Try again; if it keeps failing, contact support.`
      );
    } finally {
      setExporting(false);
    }
  };

  if (partnerId == null) {
    return <SelectPartnerPrompt />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Page header + filters — cool grey band */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 4, pb: 3 }}>
          <Typography variant="h1" gutterBottom>
            Students
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {institutionName}
          </Typography>

          {/* Filter toolbar */}
          <Stack
            direction={isSmall ? 'column' : 'row'}
            spacing={1.5}
            alignItems={isSmall ? 'stretch' : 'center'}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search by student name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i
                      className="fa-thin fa-magnifying-glass"
                      style={{ color: 'rgba(0,0,0,0.54)', fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <i className="fa-thin fa-xmark" style={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 'auto', height: 40 }}
            >
              Search
            </Button>

            <Autocomplete
              size="small"
              options={STATUS_OPTIONS}
              getOptionLabel={(opt) => opt.label}
              value={statusFilter}
              onChange={(_e, val) => {
                setStatusFilter(val);
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} placeholder="Status..." />}
              sx={{ minWidth: 160 }}
            />

            <DatePicker
              label="From"
              value={dateFrom}
              onChange={(val) => {
                setDateFrom(val);
                setPage(0);
              }}
              slots={{
                openPickerIcon: () => (
                  <i className="fa-thin fa-calendar" style={{ fontSize: 18 }} />
                ),
              }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
            <DatePicker
              label="To"
              value={dateTo}
              onChange={(val) => {
                setDateTo(val);
                setPage(0);
              }}
              slots={{
                openPickerIcon: () => (
                  <i className="fa-thin fa-calendar" style={{ fontSize: 18 }} />
                ),
              }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />

            <Autocomplete
              multiple
              size="small"
              options={availableCourses}
              getOptionLabel={(opt) => opt.title}
              value={selectedCourses}
              onChange={(_e, val) => {
                setSelectedCourses(val);
                setPage(0);
              }}
              renderInput={(params) => <TextField {...params} placeholder="Filter by course..." />}
              sx={{ minWidth: 260 }}
              limitTags={1}
            />

            <Box sx={{ flex: 1 }} />

            <Tooltip
              title={
                exporting
                  ? 'Exporting...'
                  : selectedRows.size > 0
                    ? `Export the ${selectedRows.size} checked student${selectedRows.size === 1 ? '' : 's'} as CSV`
                    : 'Export all students matching the current filters as CSV'
              }
            >
              <Typography
                component="span"
                variant="body2"
                onClick={!exporting ? handleExportCSV : undefined}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: !exporting ? 'primary.main' : 'text.disabled',
                  fontWeight: 600,
                  cursor: !exporting ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                  '&:hover': !exporting ? { textDecoration: 'underline' } : {},
                }}
              >
                {exporting ? (
                  <CircularProgress size={16} />
                ) : (
                  <i className="fa-thin fa-file-arrow-down" style={{ fontSize: 18 }} />
                )}
                {selectedRows.size > 0
                  ? `Export CSV (${selectedRows.size} selected)`
                  : 'Export CSV'}
              </Typography>
            </Tooltip>
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {search && (
                <Chip label={`Search: "${search}"`} onDelete={handleClearSearch} size="small" />
              )}
              {statusFilter && (
                <Chip
                  label={`Status: ${statusFilter.label}`}
                  onDelete={() => {
                    setStatusFilter(null);
                    setPage(0);
                  }}
                  size="small"
                />
              )}
              {dateFrom && (
                <Chip
                  label={`From: ${dateFrom.format('MMM D, YYYY')}`}
                  onDelete={handleClearDateFrom}
                  size="small"
                />
              )}
              {dateTo && (
                <Chip
                  label={`To: ${dateTo.format('MMM D, YYYY')}`}
                  onDelete={handleClearDateTo}
                  size="small"
                />
              )}
              {selectedCourses.map((c) => (
                <Chip
                  key={c.id}
                  label={c.title}
                  onDelete={() => {
                    setSelectedCourses(selectedCourses.filter((sc) => sc.id !== c.id));
                    setPage(0);
                  }}
                  size="small"
                />
              ))}
              <Chip
                label="Clear all"
                onClick={handleClearAllFilters}
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
        </Container>
      </Box>

      {/* Content area — white background */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Student / active-course counts are shown once, in the page subtitle above
              (SOPHIA-34818 removed the duplicate summary chips that lived here). */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={somePageSelected && !allPageSelected}
                        checked={allPageSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'last_name'}
                        direction={sortColumn === 'last_name' ? sortOrder : 'asc'}
                        onClick={() => handleSort('last_name')}
                      >
                        Student
                      </TableSortLabel>
                    </TableCell>
                    {!isSmall && <TableCell>Email</TableCell>}
                    <TableCell align="right">Courses</TableCell>
                    <TableCell align="right">Certified</TableCell>
                    <TableCell align="right">In Progress</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Most recent date the student was active in Sophia">
                        <TableSortLabel
                          active={sortColumn === 'last_activity'}
                          direction={sortColumn === 'last_activity' ? sortOrder : 'desc'}
                          onClick={() => handleSort('last_activity')}
                        >
                          Last Activity
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSmall ? 6 : 7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : students.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSmall ? 6 : 7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          {hasActiveFilters
                            ? 'No students match your filters.'
                            : 'No students found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedStudents.map((s) => (
                      <TableRow
                        key={s.user_id}
                        hover
                        selected={selectedRows.has(s.user_id)}
                        onClick={() => navigate(`/students/${s.user_id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.has(s.user_id)}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleSelectRow(s)}
                          />
                        </TableCell>
                        <TableCell>
                          {s.last_name}, {s.first_name}
                        </TableCell>
                        {!isSmall && (
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {s.email}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align="right">{s.course_count}</TableCell>
                        <TableCell align="right">{s.completed_count}</TableCell>
                        <TableCell align="right">{s.in_progress_count}</TableCell>
                        <TableCell align="right">
                          {s.last_activity ? formatUtcDate(s.last_activity) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={summaryData.distinct_students}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={() => {}}
              rowsPerPageOptions={[50]}
            />
          </Paper>
        </Container>
      </Box>
    </LocalizationProvider>
  );
}
